import { Building2, LogOut, Mail, MapPin, Phone, Settings, User } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/use-auth";
import { Badge } from "../ui/badge";

const roleLabels: Record<string, string> = {
  ADMIN: "Administrator",
  VALUER: "Valuer",
  VIEWER: "View Only",
};

function DetailRow({
  icon: Icon,
  value,
}: {
  icon: React.ElementType;
  value: string | null;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{value}</span>
    </div>
  );
}

export function UserMenu(): React.ReactElement | null {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const initials =
    user.givenName && user.surname
      ? `${user.givenName[0]}${user.surname[0]}`
      : user.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .slice(0, 2);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-accent/50 transition-colors"
        data-testid="user-menu-trigger"
      >
        <div className="text-right hidden sm:block">
          <p className="text-sm font-semibold leading-tight">{user.name}</p>
          <p className="text-xs text-muted-foreground">{roleLabels[user.role] ?? user.role}</p>
        </div>
        {user.photoUrl ? (
          <img src={user.photoUrl} alt={user.name} className="h-7 w-7 rounded-full object-cover ring-1 ring-border" />
        ) : (
          <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center ring-1 ring-border">
            <span className="text-[10px] font-semibold text-primary-foreground">{initials.toUpperCase()}</span>
          </div>
        )}
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border bg-white shadow-lg">
            {/* Profile header */}
            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                {user.photoUrl ? (
                  <img src={user.photoUrl} alt={user.name} className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-foreground">{initials.toUpperCase()}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <Badge variant="secondary" className="mt-0.5">
                    {roleLabels[user.role] ?? user.role}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-3 space-y-1.5 border-b">
              <DetailRow icon={Mail} value={user.email} />
              <DetailRow icon={Building2} value={user.department} />
              <DetailRow icon={User} value={user.jobTitle} />
              <DetailRow icon={MapPin} value={user.officeLocation} />
              <DetailRow icon={Phone} value={user.mobilePhone} />
              {(user.businessPhones ?? []).length > 0 && (
                <DetailRow icon={Phone} value={user.businessPhones[0] ?? null} />
              )}
            </div>

            {/* Actions */}
            <div className="p-1">
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent transition-colors"
                data-testid="user-menu-settings"
              >
                <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                Settings
              </Link>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                data-testid="user-menu-signout"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
